/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Pressable, Icon } from '@components/atoms'
import { Modal } from '@components/molecules'
import { Checkbox } from '@mui/material'
import { GridRowModel, GridRowSelectionModel, GridRowParams } from '@mui/x-data-grid'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import { BoStack } from '@components/systems/backoffice/BoStack'
import { BoFab } from '@components/systems/backoffice/BoFab'
import {
	mono,
	boFont,
	boMobile,
	boMotion,
	boShared,
	BoSizeClass,
} from '@components/systems/backoffice/backoffice.tokens'
import { GridProps } from './GridTable'
import { GridRowDetail } from './GridRowDetail'
import { GridEditSheet } from './GridEditSheet'
import {
	resolveCardModel,
	makeApiStub,
	rawValue,
	formattedValue,
	renderCellShim,
	gridUrlKey,
	isEmptyValue,
	renderStatus,
	CardModel,
} from './gridCard'

const randomId = (): string => Math.random().toString(36).substring(2, 11)

/** 한 번에 이어 붙이는 장 수 (RESPONSIVE_PLAN §5.6 1단계 클라이언트 청크) */
const CHUNK = 30

type PropsType = GridProps & { sizeClass: BoSizeClass }

interface CardProps {
	row: GridRowModel
	model: CardModel
	apiRef: any
	renderCell?: GridProps['renderCell']
	selectable: boolean
	selectMode: boolean
	/** 탭이 선택을 토글한다(폰 선택 모드). 태블릿은 체크박스만 선택, 탭은 자세히 */
	tapToggles: boolean
	selected: boolean
	/** 태블릿 두 판 — 우 판에 열려 있는 행 */
	active: boolean
	inactive: boolean
	onTap: (row: GridRowModel) => void
	onLongPress: (row: GridRowModel) => void
	onToggle: (row: GridRowModel) => void
}

/** 카드 한 장 — 3줄(제목+칩 / 부제 / 메타). 탭 = 자세히, 길게 누르기 = 선택 모드 (RESPONSIVE_PLAN §5.3) */
const CardComponent = (props: CardProps) => {
	const { row, model, apiRef } = props
	const pressRef = React.useRef<{
		timer: ReturnType<typeof setTimeout> | null
		x: number
		y: number
		fired: boolean
	}>({ timer: null, x: 0, y: 0, fired: false })

	const clearPress = () => {
		const p = pressRef.current
		if (p.timer) clearTimeout(p.timer)
		p.timer = null
	}

	const onPointerDown = (e: React.PointerEvent) => {
		if (e.button !== 0) return
		const p = pressRef.current
		p.x = e.clientX
		p.y = e.clientY
		p.fired = false
		clearPress()
		p.timer = setTimeout(() => {
			p.fired = true
			props.onLongPress(row)
		}, boMobile.longPressMs)
	}
	const onPointerMove = (e: React.PointerEvent) => {
		const p = pressRef.current
		if (!p.timer) return
		if (Math.abs(e.clientX - p.x) > 10 || Math.abs(e.clientY - p.y) > 10) clearPress()
	}
	const onPointerEnd = () => clearPress()

	const onClick = (e: React.MouseEvent) => {
		const target = e.target as HTMLElement
		if (target.closest('a, button, input, .boCardCheck')) return
		if (pressRef.current.fired) {
			pressRef.current.fired = false
			return
		}
		if (props.tapToggles) {
			if (props.selectable) props.onToggle(row)
			return
		}
		props.onTap(row)
	}

	const titleCol = model.byField[model.title]
	const statusCol = model.status ? model.byField[model.status] : null
	const subCol = model.subtitle ? model.byField[model.subtitle] : null

	return (
		<View
			className={`boCard${props.selected ? ' selected' : ''}${
				props.active ? ' active' : ''
			}${props.inactive ? ' inactive' : ''}`}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerEnd}
			onPointerCancel={onPointerEnd}
			onPointerLeave={onPointerEnd}
			onClick={onClick}
			role="button"
			tabIndex={0}
			onKeyDown={e => {
				if (e.key === 'Enter') props.onTap(row)
			}}
		>
			{props.selectMode ? (
				<View className="boCardCheck">
					<Checkbox
						checked={props.selected}
						disabled={!props.selectable}
						onChange={() => props.onToggle(row)}
						inputProps={{ 'aria-label': '선택' }}
					/>
				</View>
			) : null}
			<View className="boCardMain">
				<View className="boCardTitleRow">
					<span className="boCardTitle">
						{titleCol ? formattedValue(titleCol, row, apiRef) : String(row.id)}
					</span>
					{statusCol ? (
						<span className="boCardStatus">
							{renderStatus(statusCol, row, apiRef, props.renderCell?.[statusCol.field])}
						</span>
					) : null}
				</View>
				{subCol ? (
					<span className="boCardSub">
						{renderCellShim(subCol, row, apiRef, props.renderCell?.[subCol.field])}
					</span>
				) : null}
				{model.meta.length > 0 ? (
					<View className="boCardMeta">
						{model.meta.map(f => {
							const col = model.byField[f]
							if (!col) return null
							if (isEmptyValue(rawValue(col, row, apiRef))) return null
							const v = renderCellShim(col, row, apiRef, props.renderCell?.[f])
							if (v === '' || v === null || v === undefined) return null
							return (
								<span className="boCardMetaItem" key={f}>
									<span className="boCardMetaLabel">{String(col.headerName ?? f)}</span>
									<span className="boCardMetaValue">{v}</span>
								</span>
							)
						})}
					</View>
				) : null}
			</View>
			<Icon name="chevron-right" className="boCardChevron" />
		</View>
	)
}

const Card = React.memo(CardComponent)

/**
 * 폰·태블릿 렌더러 — 표 대신 카드 목록 + 스크롤 이어 붙이기 (RESPONSIVE_PLAN §5.3·§5.6).
 * 공개 props 는 Grid 와 같다. 데이터는 1단계 클라이언트 청크(전량 수신, 30장씩 잘라 그린다).
 */
export const GridCardList = (props: PropsType) => {
	const isPhone = props.sizeClass === 'phone'
	// 태블릿 두 판(list-detail, RESPONSIVE_PLAN §4.2·§5.5) — 페이지가 자기 자세히(onDetailClick·onRowClick)를 가지면 목록만 전폭
	const paneMode =
		props.sizeClass === 'tablet' &&
		typeof props.onDetailClick !== 'function' &&
		typeof props.onRowClick !== 'function'
	const isEditable: boolean = props.isEditable ?? true
	const checkboxSelection: boolean = props.checkboxSelection ?? false
	const title = props.title ?? ''
	const urlKey = React.useMemo(() => gridUrlKey(props.title), [props.title])

	// ── 행 (낙관적 갱신의 주체 — 데스크톱 Grid 와 같다) ──
	const [rows, setRows] = React.useState<GridRowModel[]>([...props.rows])
	React.useEffect(() => {
		setRows([...props.rows])
	}, [props.rows])

	const model = React.useMemo(() => resolveCardModel(props.columns), [props.columns])
	const apiRef = React.useMemo(() => makeApiStub(rows), [rows])

	// ── 검색 · 정렬 · 청크 ──
	const [queryInput, setQueryInput] = React.useState('')
	const [query] = useDebounce(queryInput, 200)
	const [visibleCount, setVisibleCount] = React.useState(CHUNK)

	const searchIndex = React.useMemo(() => {
		const stub = makeApiStub(rows)
		return rows.map(r =>
			model.searchFields
				.map(f => {
					const col = model.byField[f]
					return col ? formattedValue(col, r, stub) : ''
				})
				.join(' ')
				.toLowerCase(),
		)
	}, [rows, model])

	const filtered = React.useMemo(() => {
		const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
		let list = rows
		if (words.length > 0) {
			list = rows.filter((_, i) => words.every(w => searchIndex[i].includes(w)))
		}
		// 정렬 없음 — 서버가 준 순서(최신순) 그대로 (2026-09-27 사용자 결정)
		return list
	}, [rows, searchIndex, query])

	React.useEffect(() => {
		setVisibleCount(CHUNK)
	}, [query, props.rows])

	const visible = React.useMemo(
		() => filtered.slice(0, visibleCount),
		[filtered, visibleCount],
	)
	const hasMore = visibleCount < filtered.length

	const loadMore = React.useCallback(() => {
		setVisibleCount(prev => (prev < filtered.length ? prev + CHUNK : prev))
	}, [filtered.length])

	// ── 선택 ──
	const [internalSelection, setInternalSelection] = React.useState<GridRowSelectionModel>(
		[],
	)
	const selection: GridRowSelectionModel = props.rowSelectionModel ?? internalSelection
	const setSelection = React.useCallback(
		(next: GridRowSelectionModel) => {
			setInternalSelection(next)
			props.onRowSelectionModelChange?.(next)
		},
		[props.onRowSelectionModelChange],
	)
	const [selectMode, setSelectMode] = React.useState<boolean>(false)
	const effectiveSelectMode = checkboxSelection && (!isPhone || selectMode)
	const isRowSelectable = React.useCallback(
		(row: GridRowModel): boolean => {
			if (typeof props.isRowSelectable === 'function') {
				return props.isRowSelectable({ id: row.id, row } as GridRowParams)
			}
			return true
		},
		[props.isRowSelectable],
	)
	const toggleRow = React.useCallback(
		(row: GridRowModel) => {
			const has = selection.includes(row.id)
			setSelection(has ? selection.filter(id => id !== row.id) : [...selection, row.id])
		},
		[selection, setSelection],
	)
	const exitSelectMode = React.useCallback(() => {
		setSelectMode(false)
		setSelection([])
	}, [setSelection])
	const selectAll = React.useCallback(() => {
		setSelection(filtered.filter(isRowSelectable).map(r => r.id))
	}, [filtered, isRowSelectable, setSelection])

	// ── URL 이 정본: ?v=<key>:<id> 자세히 · ?e=<key>:<id|new> 편집 ──
	const [searchParams, setSearchParams] = useSearchParams()
	const viewParam = searchParams.get('v') ?? ''
	const editParam = searchParams.get('e') ?? ''
	const viewId = viewParam.startsWith(`${urlKey}:`)
		? viewParam.slice(urlKey.length + 1)
		: ''
	const editId = editParam.startsWith(`${urlKey}:`)
		? editParam.slice(urlKey.length + 1)
		: ''

	const setParam = React.useCallback(
		(key: 'v' | 'e', value: string, replace: boolean) => {
			setSearchParams(
				prev => {
					const next = new URLSearchParams(prev)
					if (value === '') next.delete(key)
					else next.set(key, `${urlKey}:${value}`)
					return next
				},
				{ replace },
			)
		},
		[setSearchParams, urlKey],
	)

	const viewRow = React.useMemo(
		() => (viewId ? rows.find(r => String(r.id) === viewId) ?? null : null),
		[rows, viewId],
	)
	const editRow = React.useMemo(
		() =>
			editId && editId !== 'new' ? rows.find(r => String(r.id) === editId) ?? null : null,
		[rows, editId],
	)
	const editOpen = editId === 'new' || editRow !== null

	// ── 탭 · 길게 누르기 ──
	const onTap = React.useCallback(
		(row: GridRowModel) => {
			if (typeof props.onDetailClick === 'function') {
				props.onDetailClick(row)
				return
			}
			if (typeof props.onRowClick === 'function') {
				const onRowClick: any = props.onRowClick
				onRowClick({ id: row.id, row } as GridRowParams, {}, {})
				return
			}
			// 두 판에서는 고르기가 페이지 이동이 아니다 — 히스토리를 쌓지 않는다
			setParam('v', String(row.id), paneMode)
		},
		[props.onDetailClick, props.onRowClick, setParam, paneMode],
	)
	const onLongPress = React.useCallback(
		(row: GridRowModel) => {
			if (!checkboxSelection) return
			if (!selectMode) setSelectMode(true)
			if (isRowSelectable(row) && !selection.includes(row.id)) {
				setSelection([...selection, row.id])
			}
		},
		[checkboxSelection, selectMode, selection, setSelection, isRowSelectable],
	)

	// ── 삭제 ──
	const [showDelete, setShowDelete] = React.useState(false)
	const [deleteTargets, setDeleteTargets] = React.useState<GridRowModel[]>([])
	const askDelete = (targets: GridRowModel[]) => {
		if (targets.length === 0) return
		setDeleteTargets(targets)
		setShowDelete(true)
	}
	const onModalDelete = React.useCallback(() => {
		const ids = deleteTargets.map(r => r.id)
		setRows(prev => prev.filter(r => !ids.includes(r.id)))
		deleteTargets.forEach(r => props.onAction?.('D', r))
		setShowDelete(false)
		setDeleteTargets([])
		if (selectMode) exitSelectMode()
		if (viewId) setParam('v', '', true)
	}, [deleteTargets, props.onAction, selectMode, exitSelectMode, viewId, setParam])

	// ── 저장 (편집 폼) — 데스크톱 processRowUpdate 와 같은 경로 ──
	const onSave = React.useCallback(
		(draft: GridRowModel) => {
			const isNew = editId === 'new'
			if (isNew) {
				const newRow = { id: randomId(), ...draft, isNew: true }
				setRows(prev => [...prev, newRow])
				props.onAction?.('I', newRow)
			} else {
				const cleaned = { ...draft }
				delete cleaned.isNew
				delete cleaned.isEdited
				setRows(prev => prev.map(r => (r.id === cleaned.id ? cleaned : r)))
				props.onAction?.('U', cleaned)
			}
			setParam('e', '', true)
		},
		[editId, props.onAction, setParam],
	)

	// ── 스크롤 이어 붙이기 ──
	// 가상 리스트(react-virtualized)를 걷어냈다 — 자기 스크롤러를 갖는 바람에 페이지 스크롤 안에 스크롤이
	// 하나 더 생겼다(2026-09-29 사용자 지적, RESPONSIVE_PLAN §2 원칙 4). 카드는 그냥 흐름에 놓고,
	// 목록 끝의 감시자가 보이면 다음 청크를 잇는다. 스크롤러는 페이지(.containerInnerView) 하나다.
	const sentinelRef = React.useRef<HTMLDivElement | null>(null)
	React.useEffect(() => {
		const node = sentinelRef.current
		if (node === null || !hasMore) return
		const io = new IntersectionObserver(
			entries => {
				if (entries.some(anEntry => anEntry.isIntersecting)) loadMore()
			},
			{ rootMargin: '600px 0px' },
		)
		io.observe(node)
		return () => io.disconnect()
	}, [hasMore, loadMore])

	const getRowClassName = props.getRowClassName
	const renderCard = (row: GridRowModel) => {
		const cls =
			typeof getRowClassName === 'function'
				? String((getRowClassName as any)({ id: row.id, row }) ?? '')
				: ''
		return (
			<View className="boCardRow" key={String(row.id)}>
				<Card
					row={row}
					model={model}
					apiRef={apiRef}
					renderCell={props.renderCell}
					selectable={isRowSelectable(row)}
					selectMode={effectiveSelectMode}
					tapToggles={effectiveSelectMode && isPhone}
					selected={selection.includes(row.id)}
					active={paneMode && String(row.id) === viewId}
					inactive={cls.includes('row-inactive')}
					onTap={onTap}
					onLongPress={onLongPress}
					onToggle={toggleRow}
				/>
			</View>
		)
	}

	const isLoading = props.loading ?? false

	return (
		<View
			css={style}
			className={`boCardList ${props.sizeClass}${paneMode ? ' pane' : ''}`}
		>
			<View className="boCardListList">
				<View className="boCardListTop">
					{effectiveSelectMode && isPhone ? (
						<View className="boCardListSelectBar">
							<Pressable
								className="boCardListIconBtn"
								onClick={exitSelectMode}
								aria-label="선택 취소"
							>
								<Icon name="close" />
							</Pressable>
							<span className="boCardListSelectCount">{selection.length} 선택</span>
							<button type="button" className="boBtnText" onClick={selectAll}>
								전체
							</button>
							{isEditable ? (
								<button
									type="button"
									className="boBtnText"
									disabled={selection.length === 0}
									onClick={() => askDelete(rows.filter(r => selection.includes(r.id)))}
								>
									삭제
								</button>
							) : null}
						</View>
					) : (
						<View className="boCardListSearchRow">
							<View className="boCardListSearch">
								<Icon name="magnify" className="boCardListSearchIcon" />
								<input
									type="search"
									className="boCardListSearchInput"
									placeholder="검색"
									value={queryInput}
									onChange={e => setQueryInput(e.target.value)}
									autoComplete="off"
									enterKeyHint="search"
								/>
								{queryInput ? (
									<Pressable
										className="boCardListIconBtn"
										onClick={() => setQueryInput('')}
										aria-label="지우기"
									>
										<Icon name="close-circle" />
									</Pressable>
								) : null}
							</View>
							{paneMode && isEditable ? (
								<button
									type="button"
									className="boBtnGhost boCardListAddBtn"
									onClick={() => setParam('e', 'new', false)}
								>
									추가
								</button>
							) : null}
							{checkboxSelection && isPhone ? (
								<Pressable
									className="boCardListIconBtn"
									onClick={() => setSelectMode(true)}
									aria-label="선택"
								>
									<Icon name="checkbox-multiple-marked-outline" />
								</Pressable>
							) : null}
						</View>
					)}
					{!isPhone && checkboxSelection && selection.length > 0 ? (
						<View className="boCardListSelectBar">
							<span className="boCardListSelectCount">{selection.length} 선택</span>
							<button type="button" className="boBtnText" onClick={selectAll}>
								전체
							</button>
							<button
								type="button"
								className="boBtnText"
								onClick={() => setSelection([])}
							>
								해제
							</button>
							{isEditable ? (
								<button
									type="button"
									className="boBtnText"
									onClick={() => askDelete(rows.filter(r => selection.includes(r.id)))}
								>
									삭제
								</button>
							) : null}
						</View>
					) : null}
				</View>

				<View className="boCardListBody">
					{isLoading ? (
						<View className="boCardListSkeleton">
							{[0, 1, 2].map(i => (
								<View className="boCardSkeleton" key={i}>
									<View className="sk sk1" />
									<View className="sk sk2" />
									<View className="sk sk3" />
								</View>
							))}
						</View>
					) : filtered.length === 0 ? (
						<View className="boEmpty">
							{query ? '검색 결과가 없다' : '데이터가 없다'}
							{query ? (
								<button
									type="button"
									className="boBtnText"
									onClick={() => setQueryInput('')}
									css={{ marginLeft: 8 }}
								>
									검색 지우기
								</button>
							) : null}
						</View>
					) : (
						<>
							{visible.map(renderCard)}
							{hasMore ? (
								<View className="boCardListSentinel" ref={sentinelRef} aria-hidden />
							) : null}
						</>
					)}
				</View>
			</View>

			{/* 태블릿 우 판 — 자세히. 안 골랐으면 한 줄 (RESPONSIVE_PLAN §4.2) */}
			{paneMode ? (
				<View className="boListPaneDetail">
					{viewRow ? (
						<>
							{isEditable ? (
								<View className="boListPaneActions">
									<button
										type="button"
										className="boBtnText"
										onClick={() => setParam('e', String(viewRow.id), false)}
									>
										수정
									</button>
									<button
										type="button"
										className="boBtnText"
										onClick={() => askDelete([viewRow])}
									>
										삭제
									</button>
								</View>
							) : null}
							<GridRowDetail
								model={model}
								row={viewRow}
								apiRef={apiRef}
								renderCell={props.renderCell}
								tabbed={false}
							/>
						</>
					) : (
						<View className="boEmpty">목록에서 고르면 여기에 보인다</View>
					)}
				</View>
			) : null}

			{isEditable && !effectiveSelectMode && !paneMode ? (
				<BoFab label="추가" onClick={() => setParam('e', 'new', false)} />
			) : null}

			{/* 자세히 — 페이지가 onDetailClick 을 안 줬을 때만 (한 depth) */}
			<BoStack
				open={viewRow !== null && !paneMode}
				onClose={() => setParam('v', '', true)}
				title={title || '자세히'}
				wideMode="sheet"
				actions={
					isEditable && viewRow ? (
						<>
							<button
								type="button"
								className="boBtnText"
								onClick={() => setParam('e', String(viewRow.id), false)}
							>
								수정
							</button>
							<button
								type="button"
								className="boBtnText"
								onClick={() => askDelete([viewRow])}
							>
								삭제
							</button>
						</>
					) : undefined
				}
			>
				{viewRow ? (
					<GridRowDetail
						model={model}
						row={viewRow}
						apiRef={apiRef}
						renderCell={props.renderCell}
						tabbed={isPhone}
					/>
				) : null}
			</BoStack>

			{/* 편집 — 한 depth 들어간 폼 */}
			<GridEditSheet
				open={editOpen}
				onClose={() => setParam('e', '', true)}
				columns={props.columns}
				model={model}
				row={editRow}
				isNew={editId === 'new'}
				apiRef={apiRef}
				renderEditCell={props.renderEditCell}
				renderEditModeCell={props.renderEditModeCell}
				onSave={onSave}
				title={title || '항목'}
			/>

			<Modal
				target="boCardDelete"
				title="삭제"
				show={showDelete}
				onSubmit={onModalDelete}
				onCloseModal={() => setShowDelete(false)}
				closeStatus="inherit"
				closeTitle="닫기"
				submitTitle="삭제"
				submitStatus="error"
				submitIcon="trash-can-outline"
			>
				<View css={{ width: '30rem', maxWidth: '100%' }}>
					<span css={{ fontSize: 14 }}>
						{deleteTargets.length > 1
							? `${deleteTargets.length}건을 삭제한다. 되돌릴 수 없다.`
							: '삭제한 데이터는 되돌릴 수 없다.'}
					</span>
				</View>
			</Modal>
		</View>
	)
}

const style = css({
	...boShared,
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	maxWidth: '100%',
	minWidth: 0,
	// 높이를 100% 로 못 박지 않는다 — 폰·태블릿(비 pane)은 페이지 스크롤러 하나에 얹힌다
	minHeight: 0,
	fontFamily: boFont.ui,

	'.boCardListList': {
		flex: '1 1 auto',
		minWidth: 0,
		minHeight: 0,
		display: 'flex',
		flexDirection: 'column',
	},

	// 검색 줄은 페이지 헤더 바로 아래에 붙어 있는다 (BoxView 가 --boHeaderH 를 준다)
	'.boCardListTop': {
		position: 'sticky',
		top: 'var(--boHeaderH, 0px)',
		zIndex: 2,
		flexShrink: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		padding: '4px 0 8px',
		background: mono.bg,
	},

	'.boCardListSearchRow': {
		display: 'flex',
		alignItems: 'center',
		gap: 4,
	},
	'.boCardListSearch': {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		height: 44,
		padding: '0 8px 0 10px',
		background: mono.surface,
		border: `1px solid ${mono.line}`,
		borderRadius: 2,
		'&:focus-within': { borderColor: mono.ink },
		'.boCardListSearchIcon': { fontSize: 20, color: mono.faint, flexShrink: 0 },
		'.boCardListSearchInput': {
			flex: 1,
			minWidth: 0,
			height: '100%',
			border: 0,
			outline: 'none',
			background: 'transparent',
			fontFamily: boFont.ui,
			fontSize: 16,
			fontWeight: 300,
			color: mono.ink,
			'&::placeholder': { color: mono.faint },
			'&::-webkit-search-cancel-button': { display: 'none' },
		},
	},
	'.boCardListIconBtn': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 44,
		height: 44,
		flexShrink: 0,
		borderRadius: 2,
		'& .mdi': { fontSize: 22, color: mono.dim },
		'&.active .mdi': { color: mono.ink },
	},

	'.boCardListSelectBar': {
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		minHeight: 44,
		'.boCardListSelectCount': {
			flex: 1,
			fontSize: 15,
			fontWeight: 500,
			paddingLeft: 4,
		},
		'.boBtnText': { height: 44, fontSize: 14 },
	},

	'.boCardListBody': {
		flex: '1 1 auto',
		minWidth: 0,
		minHeight: 0,
		position: 'relative',
	},

	'.boCardListSentinel': { height: 1, width: '100%' },

	'.boCardRow': {
		paddingBottom: boMobile.cardGap,
		boxSizing: 'border-box',
	},

	'.boCard': {
		display: 'flex',
		alignItems: 'stretch',
		gap: 4,
		minHeight: 72,
		padding: `12px 8px 12px ${boMobile.cardPad}px`,
		background: mono.surface,
		border: `1px solid ${mono.line}`,
		borderRadius: 0,
		cursor: 'pointer',
		userSelect: 'none',
		WebkitUserSelect: 'none',
		WebkitTouchCallout: 'none',
		touchAction: 'manipulation',
		transition: boMotion,
		outline: 'none',
		'&:focus-visible': { outline: `1px solid ${mono.ink}`, outlineOffset: 2 },
		'&:active': { background: mono.bg },
		'&.selected': {
			background: 'rgba(22, 24, 27, 0.05)',
			borderColor: mono.ink,
		},
		'&.inactive': { color: mono.faint },
		'&.inactive .boCardTitle, &.inactive .boCardSub, &.inactive .boCardMetaValue': {
			color: mono.faint,
		},

		'.boCardCheck': {
			display: 'flex',
			alignItems: 'center',
			marginLeft: -12,
			marginRight: 2,
		},

		'.boCardMain': {
			flex: 1,
			minWidth: 0,
			display: 'flex',
			flexDirection: 'column',
			gap: 4,
			justifyContent: 'center',
		},
		'.boCardTitleRow': {
			display: 'flex',
			alignItems: 'center',
			gap: 8,
		},
		'.boCardTitle': {
			flex: 1,
			minWidth: 0,
			fontSize: 16,
			fontWeight: 500,
			color: mono.ink,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			fontVariantNumeric: 'tabular-nums',
		},
		'.boCardStatus': {
			display: 'flex',
			gap: 4,
			flexShrink: 0,
		},
		'.boCardSub': {
			fontSize: 14,
			fontWeight: 300,
			color: mono.dim,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			'& a': { color: mono.dim },
		},
		'.boCardMeta': {
			display: 'flex',
			flexWrap: 'wrap',
			gap: '2px 12px',
			marginTop: 2,
		},
		'.boCardMetaItem': {
			display: 'inline-flex',
			alignItems: 'baseline',
			gap: 5,
			maxWidth: '100%',
			fontSize: 13,
			color: mono.dim,
			fontVariantNumeric: 'tabular-nums',
		},
		'.boCardMetaLabel': {
			fontSize: 10,
			letterSpacing: '.12em',
			textTransform: 'uppercase',
			color: mono.faint,
			fontWeight: 500,
			flexShrink: 0,
		},
		'.boCardMetaValue': {
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			display: 'inline-flex',
			gap: 4,
			'& a': { color: mono.dim },
		},
		'.boCardChevron': {
			alignSelf: 'center',
			fontSize: 22,
			color: mono.lineSoft,
			flexShrink: 0,
		},
	},

	'.boCardListSkeleton': {
		display: 'flex',
		flexDirection: 'column',
		gap: boMobile.cardGap,
		'.boCardSkeleton': {
			padding: 16,
			background: mono.surface,
			border: `1px solid ${mono.line}`,
			'.sk': {
				height: 12,
				borderRadius: 2,
				background: mono.line,
				marginBottom: 10,
				animation: 'boSkPulse 1.2s ease-in-out infinite',
			},
			'.sk1': { width: '55%', height: 16 },
			'.sk2': { width: '75%' },
			'.sk3': { width: '40%', marginBottom: 0 },
		},
	},
	'@keyframes boSkPulse': {
		'0%, 100%': { opacity: 1 },
		'50%': { opacity: 0.45 },
	},

	'&.tablet': {
		'.boCard': { minHeight: 56 },
	},

	// 태블릿 두 판 — 좌 목록 판(380, 리스트 아이템) + 우 자세히 판(자기 스크롤러) (RESPONSIVE_PLAN §4.2·§5.5)
	'&.pane': {
		flexDirection: 'row',
		alignItems: 'stretch',
		height: '100%',
		'.boCardListList': {
			flex: `0 0 ${boMobile.paneListW}px`,
			width: boMobile.paneListW,
			minWidth: 0,
			paddingRight: 16,
			borderRight: `1px solid ${mono.line}`,
		},
		'.boCardListTop': { top: 0 },
		'.boCardListBody': {
			overflowY: 'auto',
			overscrollBehaviorY: 'contain',
		},
		'.boCardListAddBtn': { height: 44, flexShrink: 0 },
		'.boCardRow': { paddingBottom: 0 },
		'.boCard': {
			minHeight: 56,
			padding: '10px 4px 10px 12px',
			background: 'transparent',
			border: 0,
			borderBottom: `1px solid ${mono.line}`,
			'&.active': {
				background: mono.bg,
				boxShadow: `inset 2px 0 0 ${mono.ink}`,
			},
			'.boCardMeta': {
				flexWrap: 'nowrap',
				overflow: 'hidden',
				'.boCardMetaItem:not(:first-of-type)': { display: 'none' },
			},
		},
		'.boListPaneDetail': {
			flex: '1 1 auto',
			minWidth: 0,
			minHeight: 0,
			overflowY: 'auto',
			padding: '0 0 24px 24px',
			'.boRowDetailHead': { paddingTop: 0 },
		},
		'.boListPaneActions': {
			display: 'flex',
			justifyContent: 'flex-end',
			gap: 8,
			minHeight: 44,
		},
	},
})
